/**
 * The Rust/Tauri command surface speaks snake_case (serde's default) everywhere — every store,
 * composable, and DTO type in `app/` (`Group`, `Message`, `role_ids`, `channel_id`, ...) is
 * written against that. The documented REST API (docs.iopta.org) speaks camelCase instead. Rather
 * than hand-writing a snake<->camel mapping for every DTO the REST API can return, every
 * `BrowserClientSdk` request/response is passed through these two converters so the rest of the
 * app never has to know the wire format changed.
 */
function toCamel(key: string): string {
  return key.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase())
}

function toSnake(key: string): string {
  return key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)
}

function deepMapKeys(value: unknown, mapKey: (key: string) => string): unknown {
  if (Array.isArray(value)) return value.map((v) => deepMapKeys(v, mapKey))
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [mapKey(k), deepMapKeys(v, mapKey)]),
    )
  }
  return value
}

/** REST response (camelCase) -> frontend shape (snake_case). */
export function camelToSnakeDeep<T = unknown>(value: unknown): T {
  return deepMapKeys(value, toSnake) as T
}

/** Frontend command args (snake_case) -> REST request body/query (camelCase). */
export function snakeToCamelDeep<T = unknown>(value: unknown): T {
  return deepMapKeys(value, toCamel) as T
}
